import React, { useState } from 'react';
import { Check, Search, Sparkles } from 'lucide-react';
import LineMatchPanel from './LineMatchPanel';
import { MATCH_LABELS, money, NOT_COSTED_REASONS } from './invoiceStatus';

const TONE = {
  matched: 'text-primary-700 bg-primary-50 border-primary-200',
  needs_match: 'text-accent-800 bg-accent-50 border-accent-200',
  needs_category: 'text-accent-800 bg-accent-50 border-accent-200',
  not_costed: 'text-ink-muted bg-surface-sunken border-surface-border',
  unmatched: 'text-ink-muted bg-surface-sunken border-surface-border',
};

function reasonLabel(v) {
  return (NOT_COSTED_REASONS.find((r) => r.value === v) || {}).label || v;
}

export default function LineRow({ view, itemName, busy, onConfirm, onCategory, canDecide = true }) {
  const [mode, setMode] = useState(null);
  const { line, index, status, suggestions } = view;
  const canAct = status === 'needs_match' && !busy;
  const i = index;

  return (
    <li className="rounded-xl border border-surface-border bg-surface p-4" data-testid={`line-row-${i}`}>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <p className="font-medium text-ink break-words" data-testid={`line-desc-${i}`}>{view.raw || 'No description'}</p>
            <span className={`shrink-0 rounded-full border px-2.5 py-0.5 text-xs font-medium ${TONE[status]}`} data-testid={`line-status-${i}`}>
              {MATCH_LABELS[status]}
            </span>
          </div>
          <p className="text-sm text-ink-muted font-mono mt-1" data-testid={`line-money-${i}`}>
            {line.qty} × {money(line.unit_price)} = <span className="text-ink">{money(line.line_total)}</span>
          </p>
          {(line.vat_amount != null || line.product_code) && (
            <p className="text-xs text-ink-muted font-mono mt-0.5" data-testid={`line-vat-${i}`}>
              {line.product_code ? `${line.product_code} · ` : ''}
              {line.vat_amount != null
                ? `VAT ${Math.round(Number(line.vat_rate || 0) * 100)}% ${money(line.vat_amount)}${line.vat_code ? ` (code ${line.vat_code})` : ''}`
                : ''}
            </p>
          )}
          {status === 'matched' && (
            <p className="text-sm text-primary-700 mt-1" data-testid={`line-item-${i}`}>
              → {itemName || line.item_name || 'Catalogue item'}
              {line.new_item && (
                <span className="ml-2 inline-flex items-center gap-1 rounded-full border border-primary-200 bg-primary-50 px-2 py-0.5 text-xs font-medium"
                  data-testid={`line-new-item-${i}`}>
                  <Sparkles className="w-3 h-3" aria-hidden="true" /> New item, added from this invoice
                </span>
              )}
              {line.new_item && line.similar_to && (
                <span className="ml-2 text-xs text-ink-muted" data-testid={`line-similar-${i}`}>not the same as {line.similar_to}</span>
              )}
              {line.matched_by === 'name' && (
                <span className="ml-2 text-xs text-ink-muted" data-testid={`line-matched-by-${i}`}>matched by name</span>
              )}
            </p>
          )}
          {status === 'not_costed' && (
            <p className="text-sm text-ink-muted mt-1" data-testid={`line-not-costed-${i}`}>{reasonLabel(line.not_costed_reason)} · no price recorded</p>
          )}
        </div>
      </div>

      {canDecide && status === 'needs_match' && (
        <div className="mt-3 flex flex-wrap gap-2">
          {suggestions.map((s, k) => (
            <button key={`${s.item_id}-${k}`} type="button" disabled={!canAct} onClick={() => onConfirm(view, s.item_id)}
              data-testid={`line-suggestion-${i}-${k}`}
              className={`inline-flex items-center gap-1.5 min-h-[44px] px-4 rounded-full text-sm font-medium disabled:opacity-50 motion-organic ${k === 0 ? 'bg-primary-500 text-surface hover:bg-primary-600' : 'border border-primary-300 text-primary-700 hover:bg-primary-50'}`}>
              <Check className="w-4 h-4" /> Confirm {s.name}
            </button>
          ))}
          <button type="button" disabled={!canAct} onClick={() => setMode(mode === 'find' ? null : 'find')} data-testid={`line-find-${i}`}
            className="inline-flex items-center gap-1.5 min-h-[44px] px-4 rounded-full border border-surface-border text-ink hover:bg-surface-sunken disabled:opacity-50">
            <Search className="w-4 h-4" /> Find or create item
          </button>
        </div>
      )}
      {status === 'needs_match' && mode === 'find' && (
        <LineMatchPanel lineView={view} onClose={() => setMode(null)}
          onPick={async (itemId) => { setMode(null); await onConfirm(view, itemId); }} />
      )}
      {canDecide && status === 'needs_category' && (
        <div className="mt-3">
          <p className="text-sm text-ink mb-2">Which category does {itemName || 'this item'} belong to?</p>
          <div className="flex flex-wrap gap-2">
            {(view.glTask.payload?.categories || []).map((c) => (
              <button key={c} type="button" disabled={busy} onClick={() => onCategory(view, c)} data-testid={`line-category-${i}-${c}`}
                className="min-h-[44px] px-4 rounded-full border border-surface-border text-ink hover:bg-primary-50 hover:border-primary-300 disabled:opacity-50">
                {c.replace(/_/g, ' ')}
              </button>
            ))}
          </div>
        </div>
      )}
    </li>
  );
}
